import { getClassFunction } from '@helpers/functions';
import { currentViewport } from '@helpers/breakpoint';

const getClass = getClassFunction('kf-o-read-more');

const elements = document.querySelectorAll(getClass('', { checkAttached: true }));

const handleExpander = (el: HTMLElement) => {
  el.dataset.eventAttached = true + '';
  const expander = el.querySelector<HTMLElement>(getClass('expander'));
  const expanderTextEl = el.querySelector<HTMLElement>(getClass('expander__text'));
  const expandedText = expanderTextEl?.getAttribute('data-expand-text');
  const collapsedText = expanderTextEl?.getAttribute('data-collapse-text');
  const contentEl = el.querySelector<HTMLElement>(getClass('content'));
  const contentContainer = el.querySelector<HTMLElement>(getClass('content-container'));
  const signPostingButton = el.querySelector<HTMLElement>(getClass('signposting-button'));
  const itemPerBatchMobile = el.dataset.itemPerBatchMobile;
  const itemFirstBatchMobile = el.dataset.itemFirstBatchMobile || itemPerBatchMobile;
  const itemPerBatchTablet = el.dataset.itemPerBatchTablet;
  const itemFirstBatchTablet = el.dataset.itemFirstBatchTablet || itemPerBatchTablet;
  const itemPerBatchDesktop = el.dataset.itemPerBatchDesktop;
  const itemClassIdentify = el.dataset.itemClassIdentify;
  const expandedAllOnDesktop = el.dataset.expandedAllOnDesktop;
  const isShowFirstBatchOnLoad = el.dataset.showFirstBatchOnLoad;
  let clickedTime: number = 0;
  let firstBatch = true;

  let itemInteractionObserver: IntersectionObserver | undefined;

  if (!contentEl) return;

  const allChildren: HTMLElement[] = [];
  const contentTreeWalker = document.createTreeWalker(contentEl, NodeFilter.SHOW_ELEMENT);

  while (contentTreeWalker.nextNode()) {
    allChildren.push(contentTreeWalker.currentNode as HTMLElement);
  }

  const initExpandedOnLoadHeight = () => {
    if (el.classList.contains('active')) {
      setContentContainerHeight();
    }
  };

  const showFirstBatchOnLoad = () => {
    if (!isShowFirstBatchOnLoad || isShowFirstBatchOnLoad !== 'true') return;

    if (
      (isShowFirstBatchOnLoad === 'true' && expandedAllOnDesktop !== 'true') ||
      (isShowFirstBatchOnLoad === 'true' && expandedAllOnDesktop === 'true' && currentViewport() !== 'desktop')
    ) {
      handleClick();
    }
  };

  const setContentContainerHeight = (height?: number, needGap?: boolean) => {
    const rowGap = parseFloat(getComputedStyle(contentEl).rowGap);

    if (!contentContainer) return;

    if (height === 0) {
      contentContainer.style.height = '0px';
    } else {
      setTimeout(() => {
        contentContainer.style.height = (height ?? contentEl.scrollHeight) + (needGap ? (Number.isNaN(rowGap) ? 0 : rowGap) : 0) + 'px';
      }, 0);
    }
  };

  const handleShowAllOnDesktop = () => {
    if (expandedAllOnDesktop === 'true' && currentViewport() === 'desktop') {
      handleShowMoreAll();
    }
  };

  const getItemPerBatch = () => {
    switch (currentViewport()) {
      case 'desktop':
        return itemPerBatchDesktop;
      case 'tablet':
        return firstBatch ? itemFirstBatchTablet : itemPerBatchTablet;
      case 'mobile':
        return firstBatch ? itemFirstBatchMobile : itemPerBatchMobile;
      default:
        return undefined;
    }
  };

  const observeByBatchContentHeight = (data: DOMRect, last: DOMRect) => {
    if (data.width !== last.width) {
      itemInteractionObserver?.disconnect();
      itemInteractionObserver = undefined;

      const allItems = Array.from(el.querySelectorAll<HTMLElement>(`.${itemClassIdentify}`)).filter((item) =>
        item.classList.contains('expander-activated')
      );

      const firstItem = allItems.at(0);
      const lastItem = allItems.at(allItems.length - 1);

      if (!firstItem || !lastItem) return;

      const totalHeight = lastItem.offsetTop + lastItem.offsetHeight - firstItem.offsetTop;

      if (contentContainer) {
        setContentContainerHeight(totalHeight);
      }
    }
  };

  const handleExpandedAllOnDesktopContentHeight = () => {
    if (expandedAllOnDesktop === 'true') {
      if (currentViewport() === 'desktop') {
        if (!itemInteractionObserver) {
          observeContentVisible();
        }

        const allItems = el.querySelectorAll<HTMLElement>(`.${itemClassIdentify}`);

        allItems.forEach((item) => {
          if (item.dataset.preventShow === 'true') {
            item.removeAttribute('data-prevent-show');
          }
        });

        handleShowMoreAll();
      }
    }
  };

  const observeContentHeight = () => {
    if (!contentContainer || !contentEl) return;

    let last = el.getBoundingClientRect();

    const resizeObserver = new ResizeObserver(() => {
      if (getItemPerBatch() && itemClassIdentify) {
        const data = el.getBoundingClientRect();

        handleExpandedAllOnDesktopContentHeight();
        observeByBatchContentHeight(data, last);

        last = data;

        return;
      }

      if (el.classList.contains('active')) {
        setContentContainerHeight();
      } else {
        setContentContainerHeight(0);
      }
    });

    resizeObserver.observe(el);
  };

  const observeContentVisible = () => {
    itemInteractionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;

          if (entry.isIntersecting) {
            if (target.dataset.preventShow !== 'true') {
              target.classList.add('expander-activated');
              target.removeAttribute('inert');
            }
          } else {
            target.classList.remove('expander-activated');
            target.setAttribute('inert', 'true');
          }
        });
      },
      {
        root: contentContainer,
        threshold: 0.1,
      }
    );

    allChildren.forEach((child) => {
      itemInteractionObserver?.observe(child);
    });
  };

  const updateFinalState = (hiddenItems: HTMLElement[]) => {
    if (!hiddenItems.length) {
      el.classList.add('active');
      el.dataset.dirty = 'true';

      if (expander) {
        expander.setAttribute('aria-pressed', 'true');
        expander.ariaLabel = collapsedText || '';

        if (collapsedText) {
          if (expanderTextEl) expanderTextEl.textContent = collapsedText;
        } else {
          expander.style.display = 'none';
        }
      }

      if (signPostingButton && (clickedTime > 1 || isShowFirstBatchOnLoad !== 'true')) {
        signPostingButton.dataset.show = 'true';
      }
    }
  };

  const handleShowMoreByBatch = (itemPerBatch: string) => {
    if (!itemClassIdentify || !contentEl) return;
    const allItems = Array.from(el.querySelectorAll<HTMLElement>(`.${itemClassIdentify}`));
    const hiddenItems = allItems.filter((item) => !item.classList.contains('expander-activated'));
    const nextItems = hiddenItems.splice(0, +itemPerBatch);
    const nextLastSibling = hiddenItems.at(0);

    nextItems.forEach((item) => {
      if (item.dataset.preventShow === 'true') {
        item.removeAttribute('data-prevent-show');
        item.classList.add('expander-activated');
      }
    });

    if (nextItems.length) {
      let firstItem = nextItems.at(0);
      const lastItem = nextItems.at(nextItems.length - 1);

      if (!firstItem || !lastItem) return;
      const prevSibling = firstItem.previousElementSibling as HTMLElement;

      // In case the grid layout has 2 columns and batch number is an odd number such as 3, the first item will be shown, but the second item won't.
      // We shouldn't use the second item's data since it will include a visible row and the calculation will be wrong.
      if (prevSibling?.offsetTop === firstItem.offsetTop) {
        firstItem = nextItems.at(1) as HTMLElement;

        if (nextLastSibling) {
          nextLastSibling.dataset.preventShow = 'true';
        }
      }

      const totalHeight = lastItem.offsetTop + lastItem.offsetHeight - firstItem?.offsetTop;

      el.dataset.dirty = 'true';

      if (contentContainer) {
        const needGap = isShowFirstBatchOnLoad !== 'true' ? true : !(isShowFirstBatchOnLoad === 'true' && hiddenItems.length && clickedTime === 1);

        setContentContainerHeight(contentContainer.offsetHeight + totalHeight, needGap);
      }
    }

    updateFinalState(hiddenItems);
  };

  const handleCloseAll = () => {
    if (!expanderTextEl) return;

    expanderTextEl.textContent = expandedText || '';

    if (expander) {
      expander.ariaLabel = expandedText || '';
    }

    el.classList.remove('active');
    expander?.setAttribute('aria-pressed', 'false');

    if (contentContainer) {
      setContentContainerHeight(0);
    }

    // Scroll to the top of the Rich Text Box
    const textBlock = el.closest('.kf-rich-text-box');

    if (textBlock) {
      textBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleShowMoreAll = () => {
    if (!expanderTextEl) return;

    el.dataset.dirty = 'true';
    expanderTextEl.textContent = collapsedText || '';
    el.classList.add('active');
    expander?.setAttribute('aria-pressed', 'true');

    if (expander) {
      expander.ariaLabel = collapsedText || '';
    }

    setContentContainerHeight();
  };

  const handleToggleByBatch = (itemPerBatch: string) => {
    if (!itemInteractionObserver) {
      observeContentVisible();
    }

    if (el.classList.contains('active')) {
      handleCloseAll();
    } else {
      handleShowMoreByBatch(itemPerBatch);
    }
  };

  const handleToggleAll = () => {
    if (el.classList.contains('active')) {
      handleCloseAll();
    } else {
      handleShowMoreAll();
    }
  };

  const initContentTileElementHeight = () => {
    if (!contentEl) return;

    const contentTilesBlock = contentEl.closest('.kf-o-content-tiles');

    if (!contentTilesBlock) return;

    const contentTileEl = contentTilesBlock.querySelector<HTMLDivElement>('.col-gutter-lr');

    if (!contentTileEl) return;

    const contentTileBoundingRect = contentTileEl.getBoundingClientRect();

    contentEl.style.setProperty('--item-height', `${contentTileBoundingRect.height}px`);
  };

  const handleClick = () => {
    const itemPerBatch = getItemPerBatch();

    clickedTime += 1;

    // first batch loaded so set it to false
    firstBatch = false;

    if (itemPerBatch) {
      handleToggleByBatch(itemPerBatch);
    } else {
      handleToggleAll();
    }
  };

  expander?.addEventListener('click', handleClick);
  expander?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.code === 'Enter') return handleClick();
    if (e.code === 'Space') {
      e.preventDefault();

      return handleClick();
    }
  });

  initContentTileElementHeight();
  observeContentVisible();
  observeContentHeight();
  initExpandedOnLoadHeight();
  handleShowAllOnDesktop();
  showFirstBatchOnLoad();

  el.observeResize(() => {
    initContentTileElementHeight();
  });
};

[].forEach.call(elements, (el) => handleExpander(el));
